import React, { useState, useEffect, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import Preloader from "../../components/CustomComponents/preLoad/preLoader";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast";
import FormInput from "../../components/CustomComponents/Input/formInput";
import { Panel, PanelHeader, PanelBody } from "../../components/panel/panel";
import useDarkMode from "../../hooks/useDarkMode";
import RequisitionTabBar from "./requisitionTabBar";
import swal from "sweetalert";
import Select from "react-select";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles";
import "./requisition-pages.css";

const UpdateRequisition = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const compID = useSelector((state) => state.auth.user?.userData?.compID);
  const currentUserName = useSelector((state) => state.auth.user?.userData?.userName);
  const profileData = useSelector((state) => state.auth.profileData);
  const parentCompId = profileData?.parentComID ?? profileData?.ParentComID ?? 0;
  const isMain = !parentCompId || parseInt(parentCompId, 10) <= 0;
  const isDarkMode = useDarkMode();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [header, setHeader] = useState(null);
  const [requisitionNumber, setRequisitionNumber] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [notes, setNotes] = useState("");
  const [branchOptions, setBranchOptions] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);

  const fetchBranches = useCallback(async () => {
    if (!isMain) return;
    try {
      const res = await axiosInstance.get(APIs.GET_BRANCHES);
      if (res?.data?.status && Array.isArray(res.data.data)) {
        setBranchOptions(
          res.data.data.map((b) => ({
            value: b.compID ?? b.CompID,
            label: b.compName ?? b.CompName ?? `Branch ${b.compID ?? b.CompID}`,
          }))
        );
      }
    } catch (e) {
      console.warn("Failed to load branches", e);
    }
  }, [isMain]);

  const fetchRequisition = useCallback(async () => {
    if (!id || !compID) return;
    try {
      setLoading(true);
      const res = await axiosInstance.get(APIs.GET_REQUISITION_BY_ID, {
        params: { requisitionId: id, compId: compID },
      });
      const data = res?.data?.data;
      const h = data?.header ?? data?.Header ?? data;
      if (h) {
        setHeader(h);
        setRequisitionNumber(h.requisition_Number ?? h.Requisition_Number ?? "");
        const d = h.dueDate ?? h.DueDate;
        setDueDate(d ? new Date(d).toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10));
        setNotes(h.notes ?? h.Notes ?? "");
        const branchId = h.requestingBranchCompID ?? h.RequestingBranchCompID ?? null;
        if (branchId) {
          setSelectedBranch({
            value: branchId,
            label: `Branch ${branchId}`,
          });
        }
      }
    } catch (e) {
      console.error(e);
      showErrorToast("Failed to load requisition.");
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  }, [id, compID]);

  useEffect(() => {
    fetchBranches();
  }, [fetchBranches]);

  useEffect(() => {
    fetchRequisition();
  }, [fetchRequisition]);

  useEffect(() => {
    if (!isMain || !header || branchOptions.length === 0) return;
    const branchId = header?.requestingBranchCompID ?? header?.RequestingBranchCompID;
    if (!branchId) return;
    const branchOption = branchOptions.find((b) => parseInt(b.value, 10) === parseInt(branchId, 10));
    if (branchOption) setSelectedBranch(branchOption);
  }, [branchOptions, header, isMain]);

  const handleUpdate = async () => {
    if (!id || !compID) return;
    try {
      setLoading(true);
      await axiosInstance.put(APIs.PUT_UPDATE_REQUISITION, {
        RequisitionId: parseInt(id, 10),
        CompId: compID,
        RequestingBranchCompID: isMain ? selectedBranch?.value ?? null : null,
        RequisitionNumber: requisitionNumber?.trim() || null,
        DueDate: dueDate ? new Date(dueDate).toISOString() : null,
        Notes: notes || null,
      });
      showSuccessToast("Requisition updated.");
      fetchRequisition();
    } catch (e) {
      showErrorToast(e?.response?.data?.message ?? "Update failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!id || !compID) return;
    try {
      setLoading(true);
      await axiosInstance.post(APIs.POST_SUBMIT_REQUISITION, {
        RequisitionId: parseInt(id, 10),
        CompId: compID,
      });
      showSuccessToast("Requisition submitted.");
      fetchRequisition();
    } catch (e) {
      showErrorToast(e?.response?.data?.message ?? "Submit failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!id || !compID) return;
    try {
      setLoading(true);
      await axiosInstance.post(APIs.POST_ACCEPT_REQUISITION, {
        RequisitionId: parseInt(id, 10),
        CompId: compID,
        AcceptedBy: currentUserName || "System",
      });
      showSuccessToast("Requisition accepted.");
      fetchRequisition();
    } catch (e) {
      showErrorToast(e?.response?.data?.message ?? "Accept failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    const ok = await swal({
      title: "Cancel Requisition?",
      text: "This will set status to Cancelled.",
      icon: "warning",
      buttons: { cancel: "No", confirm: "Yes" },
      dangerMode: true,
    });
    if (!ok) return;
    try {
      setLoading(true);
      await axiosInstance.put(APIs.PUT_UPDATE_REQUISITION, {
        RequisitionId: parseInt(id, 10),
        CompId: compID,
        Status: "Cancelled",
      });
      showSuccessToast("Requisition cancelled.");
      fetchRequisition();
    } catch (e) {
      showErrorToast(e?.response?.data?.message ?? "Cancel failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleClone = async () => {
    if (!id || !compID) return;
    const ok = await swal({
      title: "Clone requisition?",
      text: "A new requisition will be created with the same branch, due date, notes prefix, and line items (send/receive quantities cleared).",
      icon: "info",
      buttons: { cancel: "Cancel", confirm: "Clone" },
    });
    if (!ok) return;
    try {
      setLoading(true);
      const res = await axiosInstance.post(APIs.POST_CLONE_REQUISITION, {
        SourceRequisitionId: parseInt(id, 10),
        CompId: compID,
        CreatedBy: currentUserName || "System",
      });
      if (!res?.data?.status) {
        showErrorToast(res?.data?.message ?? "Clone failed.");
        return;
      }
      const created = res?.data?.data;
      const newId = created?.requisition_ID ?? created?.Requisition_ID;
      showSuccessToast("Requisition cloned.");
      if (newId) navigate(`/requisition/updateRequisitionItems/${newId}`);
    } catch (e) {
      showErrorToast(e?.response?.data?.message ?? "Clone failed.");
    } finally {
      setLoading(false);
    }
  };

  const status = header?.status ?? header?.Status ?? "";
  const reqNum = header?.requisition_Number ?? header?.Requisition_Number ?? `REQ-${id}`;

  const renderBreadcrumb = () => (
    <div className="d-flex align-items-center mb-3 flex-wrap gap-2">
      <div>
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <Link to="/dashboard">Home</Link>
          </li>
          <li className="breadcrumb-item">
            <Link to="/requisition/manageRequisitions">Requisitions</Link>
          </li>
          <li className="breadcrumb-item active">General</li>
        </ol>
        <h1 className="page-header mb-0">{header ? reqNum : "Requisition"}</h1>
      </div>
      <div className="ms-auto req-toolbar">
        {status === "Draft" && (
          <button type="button" onClick={handleSubmit} className="btn req-btn req-btn-info">
            <i className="fas fa-paper-plane" />
            Submit
          </button>
        )}
        {isMain && status === "Submitted" && (
          <button type="button" onClick={handleAccept} className="btn req-btn req-btn-success">
            <i className="fas fa-check" />
            Accept
          </button>
        )}
        {(status === "Draft" || status === "Submitted") && (
          <button type="button" onClick={handleCancel} className="btn req-btn req-btn-danger-plain">
            Cancel
          </button>
        )}
        <button type="button" onClick={handleClone} className="btn req-btn req-btn-outline-info req-toolbar-ghost">
          <i className="fa fa-copy" />
          Clone
        </button>
        <button type="button" onClick={handleUpdate} className="btn req-btn req-btn-primary req-toolbar-ghost">
          Save
        </button>
      </div>
    </div>
  );

  const renderBasicInfoPanel = () => (
    <Panel className="mb-0 border-0 shadow-none">
      <PanelHeader noButton className="req-panel-header">
        Basic information
        {header && (
          <span className="ms-2 opacity-90 fw-normal" style={{ fontSize: "14px" }}>
            · {status}
          </span>
        )}
      </PanelHeader>
      <PanelBody className="req-panel-body">
        <div className="row g-4">
          <div className="col-md-6">
            <FormInput
              label="Requisition Number"
              type="text"
              value={requisitionNumber}
              onchange={(e) => setRequisitionNumber(e.target.value)}
              placeHolder="e.g. REQ-001"
            />
          </div>
          <div className="col-md-6">
            <FormInput
              label="Due Date"
              type="date"
              value={dueDate}
              onchange={(e) => setDueDate(e.target.value)}
            />
          </div>
          {isMain && (
            <div className="col-md-6">
              <label className="form-label fw-medium" style={{ color: isDarkMode ? "#ffffff" : "#000000" }}>
                Requesting Branch
              </label>
              <Select
                value={selectedBranch}
                onChange={setSelectedBranch}
                options={branchOptions}
                styles={dropdownStyles}
                placeholder="Select branch"
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>
          )}
          <div className="col-12">
            <label className="form-label fw-medium" style={{ color: isDarkMode ? "#ffffff" : "#000000" }}>
              Notes
            </label>
            <textarea
              className="form-control"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes"
              style={{
                borderRadius: "8px",
                border: isDarkMode ? "1px solid #404040" : "1px solid #D9D9D9",
                background: isDarkMode ? "#2d3436" : "#F9F9F9",
                color: isDarkMode ? "#ffffff" : "#000000",
                padding: "8px 12px",
              }}
            />
          </div>
        </div>
      </PanelBody>
    </Panel>
  );

  if (initialLoading) {
    return (
      <div className={`requisition-page ${isDarkMode ? "requisition-page--dark" : ""}`}>
        <Preloader />
      </div>
    );
  }

  return (
    <div className={`requisition-page ${isDarkMode ? "requisition-page--dark" : ""}`}>
      {loading && <Preloader />}
      {renderBreadcrumb()}
      {id && <RequisitionTabBar id={id} />}
      <div className="card border-0" style={{ position: "relative", zIndex: 1 }}>
        <div className="tab-content p-3">
          <div className="tab-pane fade show active">{renderBasicInfoPanel()}</div>
        </div>
      </div>
    </div>
  );
};

export default UpdateRequisition;
